/**
 * Phase B.5 — match a free-text query against contact signals to find
 * every person tied to a topic, even when the user has forgotten the
 * name.
 *
 * Mental model: you know WHAT you're looking for (the topic), not
 * WHO. "climbing" returns everyone whose signals contain climbing.
 * "went to Stanford" returns everyone whose `currentSchool` signal
 * matches Stanford. "I met that guy who loves jazz" — just type
 * "jazz". This is where the accumulated signal graph from Phase B.2+
 * pays off: the data already exists, we just let users find it by
 * the attribute they remember.
 *
 * Pure function. Callers fetch signals via `listAllSignals` and pass
 * them in alongside the query.
 */
import type { ContactSignal } from "@/types/voice";

export interface TopicSearchMatch {
  contactId: string;
  /** The specific signals that matched the query. Dedup by value. */
  matchedSignals: MatchedSignal[];
  /** Total number of matching signals (before per-value dedup). */
  matchStrength: number;
}

export interface MatchedSignal {
  signalType: string;
  value: string;
  sourceQuote: string | null;
}

/**
 * Filter signals by a fuzzy-ish substring match against value. Case-
 * and whitespace-insensitive; a multi-word query matches when EVERY
 * word appears in the value (AND semantics — "climbing nyc" matches
 * only signals with both). Results are grouped by contactId and
 * sorted by match strength desc.
 */
export function searchSignalsByTopic(
  signals: ContactSignal[],
  query: string
): TopicSearchMatch[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return [];
  const words = normalized.split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];

  const byContact = new Map<string, TopicSearchMatch>();

  for (const s of signals) {
    if (!s.contactId) continue;
    const haystack = `${s.value} ${s.sourceQuote ?? ""}`.toLowerCase();
    const allMatch = words.every((w) => haystack.includes(w));
    if (!allMatch) continue;

    const existing = byContact.get(s.contactId);
    if (existing) {
      existing.matchStrength += 1;
      if (
        !existing.matchedSignals.some(
          (m) =>
            m.value.toLowerCase() === s.value.toLowerCase() &&
            m.signalType === s.signalType
        )
      ) {
        existing.matchedSignals.push({
          signalType: s.signalType,
          value: s.value,
          sourceQuote: s.sourceQuote,
        });
      }
    } else {
      byContact.set(s.contactId, {
        contactId: s.contactId,
        matchedSignals: [
          {
            signalType: s.signalType,
            value: s.value,
            sourceQuote: s.sourceQuote,
          },
        ],
        matchStrength: 1,
      });
    }
  }

  return Array.from(byContact.values()).sort(
    (a, b) => b.matchStrength - a.matchStrength
  );
}

/**
 * Human-readable reason label for a matched signal — renders as the
 * secondary line on a search result row.
 *   { signalType: "interest", value: "climbing" } → "Interested in climbing"
 *   { signalType: "currentCity", value: "Brooklyn" } → "Lives in Brooklyn"
 */
export function formatMatchReason(m: MatchedSignal): string {
  switch (m.signalType) {
    case "currentJob":
      return `Works at ${m.value}`;
    case "currentSchool":
      return `Studies at ${m.value}`;
    case "currentCity":
      return `Lives in ${m.value}`;
    case "interest":
      return `Interested in ${m.value}`;
    case "skill":
      return `Skilled in ${m.value}`;
    case "preference":
      return `Prefers ${m.value}`;
    case "belief":
      return `Believes: ${m.value}`;
    case "relationship":
      return `Relationship: ${m.value}`;
    case "lifeEvent":
      return m.value;
    default:
      return m.value;
  }
}
